import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import CategorySelect from "@/components/CategorySelect";

const packingOptions = ["Strip", "Bottle", "Box", "Piece", "Vial", "Ampoule", "Pack"];
const baseOptions = ["Tablet", "Capsule", "Bottle", "Vial", "Ampoule", "Piece"];
const gstOptions = [0, 5, 12, 18];
const productTypes = ["Tablet", "Capsule", "Syrup", "Injection", "Box Item", "General Product"];
const productDefaults = {
    Tablet: { packing_unit: "Strip", base_unit: "Tablet", units_per_pack: 10 },
    Capsule: { packing_unit: "Strip", base_unit: "Capsule", units_per_pack: 10 },
    Syrup: { packing_unit: "Bottle", base_unit: "Bottle", units_per_pack: 1 },
    Injection: { packing_unit: "Vial", base_unit: "Vial", units_per_pack: 1 },
    "Box Item": { packing_unit: "Box", base_unit: "Piece", units_per_pack: 1 },
    "General Product": { packing_unit: "Piece", base_unit: "Piece", units_per_pack: 1 },
};
const compatibleBaseUnits = {
    Strip: ["Tablet", "Capsule"],
    Bottle: ["Bottle"],
    Vial: ["Vial"],
    Ampoule: ["Ampoule"],
    Box: ["Piece"],
    Pack: ["Piece"],
    Piece: ["Piece"],
};

const emptyMedicine = {
    name: "",
    generic_name: "",
    product_type: "General Product",
    manufacturer: "",
    batch_no: "",
    hsn_code: "",
    category: "",
    unit: "Strip",
    mrp: 0,
    purchase_price: 0,
    gst_percent: 12,
    stock: 0,
    low_stock_threshold: 10,
    expiry_date: "",
    packing_unit: "Piece",
    base_unit: "Piece",
    units_per_pack: 1,
    allowed_selling_units: ["Piece"],
    price_calc_method: "automatic",
    base_unit_selling_price: 0,
    base_unit_purchase_price: 0,
};

export const getEmptyMedicine = () => ({ ...emptyMedicine, allowed_selling_units: [...emptyMedicine.allowed_selling_units] });

const isLooseSellingCombinationValid = (packingUnit, baseUnit) => {
    if (!packingUnit || !baseUnit || packingUnit === baseUnit) return false;
    const allowed = compatibleBaseUnits[packingUnit];
    return !allowed || allowed.includes(baseUnit);
};

const normalizeMedicine = (medicine, defaultName = "") => {
    const merged = {
        ...getEmptyMedicine(),
        ...(medicine || {}),
        name: medicine?.name ?? defaultName,
        generic_name: medicine?.generic_name || "",
        product_type: medicine?.product_type || "General Product",
        expiry_date: medicine?.expiry_date || "",
    };
    const looseAllowed = isLooseSellingCombinationValid(merged.packing_unit, merged.base_unit);
    return {
        ...merged,
        allowed_selling_units: looseAllowed
            ? (merged.allowed_selling_units || [merged.packing_unit])
            : [merged.packing_unit],
    };
};

export default function MedicineMasterForm({
    medicine,
    medicineId,
    defaultName = "",
    showStock = true,
    showPurchaseSpecificFields = true,
    requireHsn = false,
    title,
    submitLabel,
    onCancel,
    onSaved,
}) {
    const [form, setForm] = useState(() => normalizeMedicine(medicine, defaultName));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(normalizeMedicine(medicine, defaultName));
    }, [medicine, defaultName]);

    const applyProductType = (productType) => {
        const defaults = productDefaults[productType] || productDefaults["General Product"];
        setForm((prev) => ({
            ...prev,
            product_type: productType,
            packing_unit: defaults.packing_unit,
            unit: defaults.packing_unit,
            base_unit: defaults.base_unit,
            units_per_pack: defaults.units_per_pack,
            allowed_selling_units: [defaults.packing_unit],
        }));
    };

    const updatePackingUnit = (val) => {
        setForm((prev) => {
            const newP = val;
            const base = prev.base_unit || "Tablet";
            const looseAllowed = isLooseSellingCombinationValid(newP, base);
            const isBoth = looseAllowed && prev.allowed_selling_units && prev.allowed_selling_units.length > 1;
            return { ...prev, packing_unit: newP, unit: newP || "Strip", allowed_selling_units: isBoth ? [newP, base] : [newP] };
        });
    };

    const updateBaseUnit = (val) => {
        setForm((prev) => {
            const p = prev.packing_unit || "Strip";
            const newB = val;
            const looseAllowed = isLooseSellingCombinationValid(p, newB);
            const isBoth = looseAllowed && prev.allowed_selling_units && prev.allowed_selling_units.length > 1;
            return { ...prev, base_unit: newB, allowed_selling_units: isBoth ? [p, newB] : [p] };
        });
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Brand Name is required");
        if (!form.generic_name.trim()) return toast.error("Generic Name / Composition is required");
        if (requireHsn && !form.hsn_code.trim()) return toast.error("HSN Code is required");

        const packingUnit = form.packing_unit || "Piece";
        const baseUnit = form.base_unit || "Piece";
        const looseAllowed = isLooseSellingCombinationValid(packingUnit, baseUnit);
        const wantsLoose = form.allowed_selling_units && form.allowed_selling_units.length > 1;
        if (wantsLoose && !looseAllowed) return toast.error("Loose selling is not valid for the selected Packing Unit and Base Unit");

        const payload = {
            ...form,
            name: form.name.trim(),
            generic_name: form.generic_name.trim(),
            hsn_code: form.hsn_code.trim(),
            product_type: form.product_type || "General Product",
            mrp: Number(form.mrp),
            purchase_price: Number(form.purchase_price),
            gst_percent: Number(form.gst_percent),
            low_stock_threshold: Number(form.low_stock_threshold),
            units_per_pack: Number(form.units_per_pack || 10),
            allowed_selling_units: wantsLoose && looseAllowed ? [packingUnit, baseUnit] : [packingUnit],
            price_calc_method: form.price_calc_method || "automatic",
            base_unit_selling_price: form.price_calc_method === "manual" ? Number(form.base_unit_selling_price || 0) : undefined,
            base_unit_purchase_price: form.price_calc_method === "manual" ? Number(form.base_unit_purchase_price || 0) : undefined,
        };

        if (showStock) {
            payload.stock = Number(form.stock);
        }
        if (showPurchaseSpecificFields) {
            payload.batch_no = form.batch_no || "";
            payload.expiry_date = form.expiry_date || null;
        }

        setSaving(true);
        try {
            const { data } = medicineId
                ? await api.put(`/medicines/${medicineId}`, payload)
                : await api.post("/medicines", payload);
            onSaved?.(data);
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogContent className="max-w-2xl" style={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }} data-testid="medicine-dialog">
            <DialogHeader style={{ flexShrink: 0 }}>
                <DialogTitle>{title || (medicineId ? "Edit Medicine" : "Add Medicine")}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }} tabIndex={-1}>
                <div className="grid grid-cols-2 gap-4 content-start px-1">
                    <Field label="Brand Name *"><Input data-testid="med-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Generic Name / Composition *"><Input value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} /></Field>
                    <Field label="Product Type"><select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.product_type || "General Product"} onChange={(e) => applyProductType(e.target.value)}>{productTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select></Field>
                    <Field label="Manufacturer"><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></Field>
                    <Field label={`HSN Code${requireHsn ? " *" : ""}`}><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} /></Field>
                    {showPurchaseSpecificFields && <Field label="Batch No."><Input value={form.batch_no} onChange={(e) => setForm({ ...form, batch_no: e.target.value })} /></Field>}
                    <Field label="Category"><CategorySelect value={form.category} onChange={(category) => setForm({ ...form, category })} testId="med-category" /></Field>
                    <Field label="Packing Unit"><UnitSelect options={packingOptions} value={form.packing_unit} onChange={updatePackingUnit} /></Field>
                    <Field label="Base Unit"><UnitSelect options={baseOptions} value={form.base_unit} onChange={updateBaseUnit} /></Field>
                    <Field label="Units Per Pack"><Input type="number" min="1" value={form.units_per_pack || ""} onChange={(e) => setForm({ ...form, units_per_pack: Number(e.target.value) })} /></Field>
                    <Field label="Allowed Selling Units">
                        <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.allowed_selling_units && form.allowed_selling_units.length > 1 ? "both" : "packing_only"} onChange={(e) => {
                            const p = form.packing_unit || "Strip";
                            const b = form.base_unit || "Tablet";
                            const looseAllowed = isLooseSellingCombinationValid(p, b);
                            setForm({ ...form, allowed_selling_units: e.target.value === "both" && looseAllowed ? [p, b] : [p] });
                        }}>
                            <option value="packing_only">{(form.packing_unit || "Strip")} only</option>
                            <option value="both" disabled={!isLooseSellingCombinationValid(form.packing_unit, form.base_unit)}>{(form.packing_unit || "Strip")} + {(form.base_unit || "Tablet")}</option>
                        </select>
                        {!isLooseSellingCombinationValid(form.packing_unit, form.base_unit) && <div className="text-[10px] text-muted-foreground mt-1">Loose selling disabled for this packing/base unit combination.</div>}
                    </Field>
                    <Field label="Price Calculation Method"><select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.price_calc_method || "automatic"} onChange={(e) => setForm({ ...form, price_calc_method: e.target.value })}><option value="automatic">Automatic (Pack Price ÷ Units Per Pack)</option><option value="manual">Manual (Custom Base Unit Price)</option></select></Field>
                    <Field label={`MRP / ${form.packing_unit || "Pack"} (₹)`}><Input type="number" step="0.01" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} />{form.price_calc_method !== "manual" && Number(form.units_per_pack || 10) > 0 && <div className="text-[10px] text-muted-foreground mt-1">Auto: 1 {form.base_unit || "Tablet"} = ₹{(Number(form.mrp || 0) / Number(form.units_per_pack || 10)).toFixed(2)}</div>}</Field>
                    <Field label={`Purchase Price / ${form.packing_unit || "Pack"} (₹)`}><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />{form.price_calc_method !== "manual" && Number(form.units_per_pack || 10) > 0 && <div className="text-[10px] text-muted-foreground mt-1">Auto: 1 {form.base_unit || "Tablet"} = ₹{(Number(form.purchase_price || 0) / Number(form.units_per_pack || 10)).toFixed(2)}</div>}</Field>
                    {form.price_calc_method === "manual" && <><Field label={`Base Unit Selling Price / ${form.base_unit || "Tablet"} (₹)`}><Input type="number" step="0.01" value={form.base_unit_selling_price || ""} onChange={(e) => setForm({ ...form, base_unit_selling_price: e.target.value })} /></Field><Field label={`Base Unit Purchase Price / ${form.base_unit || "Tablet"} (₹)`}><Input type="number" step="0.01" value={form.base_unit_purchase_price || ""} onChange={(e) => setForm({ ...form, base_unit_purchase_price: e.target.value })} /></Field></>}
                    <Field label="GST %"><select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={String(form.gst_percent)} onChange={(e) => setForm({ ...form, gst_percent: Number(e.target.value) })}>{gstOptions.map((gst) => <option key={gst} value={gst}>{gst}%</option>)}</select></Field>
                    {showStock && <Field label="Stock"><Input data-testid="med-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></Field>}
                    <Field label="Low Stock Alert"><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} /></Field>
                    {showPurchaseSpecificFields && <Field label="Expiry Date"><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>}
                </div>
            </div>
            <DialogFooter className="mt-4" style={{ flexShrink: 0 }}>
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="save-medicine">{saving ? "Saving…" : (submitLabel || (medicineId ? "Update" : "Save"))}</Button>
            </DialogFooter>
        </DialogContent>
    );
}

function UnitSelect({ options, value, onChange }) {
    return (
        <div className="flex gap-2">
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={options.includes(value) ? value : "Other"} onChange={(e) => onChange(e.target.value === "Other" ? "" : e.target.value)}>
                {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                <option value="Other">Other...</option>
            </select>
            {(!options.includes(value) || value === "") && <Input placeholder="Specify unit" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-1/2" />}
        </div>
    );
}

function Field({ label, children }) {
    return <div><Label className="text-xs text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>;
}
